import React, { FC, useCallback, useState } from "react";
import { nanoid } from "@reduxjs/toolkit";
import { useDropzone } from "react-dropzone";
import { Gradient } from "@Components/ChannelHeader/ChannelHeader.styles";
import { Box, Grid } from "@mui/material";
import ImageCropDialog from "@Components/FileUploader/ImageCropDialog";
import CoverDraft from "./CoverDraft";
import ProfileImageDraft from "./ProfileImageDraft";
import { readFile } from "@Components/FileUploader/Utils/canvasUtils";
import { ImageType } from "@Interfaces/Entities/Images";

import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { useTranslation } from "react-i18next";
import { CHANNEL_DEFAULT_IMAGE_DATA_URI } from "../../../Utils/assets";
import { CHANNEL_COVER_ASPECT_RATIO } from "@Utils/channelCover";

interface UploadCoverProps {
    coverUrl: string;
    profileImgUrl: string;
    emptyCoverUrl: () => void;
    onSetBuffer: (bufferIgm: any) => void;
    hue: number;
    imageType: ImageType;
    userProfileImgUrl: string;
}

const UploadCoverDialogContent: FC<UploadCoverProps> = ({
    onSetBuffer,
    coverUrl,
    profileImgUrl,
    emptyCoverUrl,
    hue,
    imageType,
    userProfileImgUrl,
}) => {
    const { t } = useTranslation();
    const [imageSrc, setImageSrc] = useState<any | null>(null);
    const [draftImage, setDraftImage] = useState<any>(null);

    const handleCropCancel = () => {
        setImageSrc(null);
    };

    const getRandomHue = (min: number, max: number) => {
        return Math.floor(hue * (max - min + 1)) + min;
    };

    const handleSetCroppedImageFor = async (croppedImageUrl: string) => {
        const newImage = { src: croppedImageUrl };
        emptyCoverUrl();
        setDraftImage(newImage);
        setImageSrc(null);
    };

    const handleDraftImageRemove = () => {
        emptyCoverUrl();
        setDraftImage(null);
    };

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const selectedFile: File = acceptedFiles[0];
        let imageDataUrl = (await readFile(selectedFile)) as string;
        setImageSrc(imageDataUrl);
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: "image/*",
        maxFiles: 1,
        multiple: false,
    });

    const ImagePreview = () => {
        switch (imageType) {
            case ImageType.ChannelCover:
                return draftImage || coverUrl ? (
                    <CoverDraft
                        draftImage={coverUrl ? { src: coverUrl } : draftImage}
                        onRemoveDraftCover={handleDraftImageRemove}
                    />
                ) : (
                    <Gradient hue={getRandomHue(1, 360)}></Gradient>
                );

            case ImageType.ChannelProfile:
                return draftImage || profileImgUrl ? (
                    <ProfileImageDraft
                        draftImage={
                            profileImgUrl ? { src: profileImgUrl } : draftImage
                        }
                        onRemoveDraftProfileImage={handleDraftImageRemove}
                    />
                ) : (
                    <img
                        src={CHANNEL_DEFAULT_IMAGE_DATA_URI}
                        alt="preview"
                        style={{
                            position: "relative",
                            height: "200px",
                            width: "200px",
                            display: "block",
                            margin: "auto",
                            border: "0.1px solid lightgray",
                            borderRadius: "50%",
                        }}
                    />
                );

            case ImageType.UserProfile:
                return draftImage || userProfileImgUrl ? (
                    <ProfileImageDraft
                        draftImage={
                            userProfileImgUrl
                                ? { src: userProfileImgUrl }
                                : draftImage
                        }
                        onRemoveDraftProfileImage={handleDraftImageRemove}
                    />
                ) : (
                    <img
                        src="/images/edit-image.png"
                        alt="user profile"
                        style={{
                            position: "relative",
                            height: "200px",
                            width: "200px",
                            display: "block",
                            margin: "auto",
                            border: "0.1px solid lightgray",
                            borderRadius: "50%",
                        }}
                    />
                );

            default:
                return <></>;
        }
    };

    return (
        <Box
            sx={{
                m: 2,
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    width: "500px",
                    maxWidth: "100%",
                    height:
                        imageType === ImageType.ChannelCover ? "auto" : "200px",
                    aspectRatio:
                        imageType === ImageType.ChannelCover
                            ? `${CHANNEL_COVER_ASPECT_RATIO}`
                            : "auto",
                    alignSelf: "center",
                    "@media (max-width: 900px)": {
                        width: "100%",
                    },
                }}
            >
                <ImagePreview />
            </Box>

            <Box
                {...getRootProps()}
                sx={{
                    height: 100,
                    cursor: "pointer",
                    mt: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#4c4e53",
                    backgroundColor: "#f0f1f5",
                    borderRadius: "8px",
                    textAlign: "center",
                    boxSizing: "border-box",
                    "@media (max-width: 600px)": {
                        px: "8px",
                    },
                }}
            >
                <input {...getInputProps()} />
                <p>{t("dialogs.drag_drop")}</p>
            </Box>

            {imageType === ImageType.UserProfile ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "70%",
                        margin: "20px auto",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ color: "#22C2FC", mb: "30px" }}
                    >
                        {t("dialogs.select_avatar")}
                    </Typography>
                    <Grid container spacing={2}>
                        {Array.from(Array(18).keys()).map((_, i) => (
                            <Grid key={nanoid()} item xs={3} spacing={1}>
                                <Avatar
                                    alt={`avatar_${i}`}
                                    src={`/images/avatars/${i + 2}.svg`}
                                    sx={{ cursor: "pointer" }}
                                    onClick={() =>
                                        setImageSrc(
                                            `/images/avatars/${i + 2}.svg`
                                        )
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : null}

            {imageSrc && (
                <ImageCropDialog
                    imageUrl={imageSrc}
                    handleCancel={handleCropCancel}
                    setCroppedImageFor={handleSetCroppedImageFor}
                    setBuffer={onSetBuffer}
                    aspect={
                        imageType === ImageType.ChannelCover
                            ? CHANNEL_COVER_ASPECT_RATIO
                            : 1
                    }
                    cropShape={
                        imageType === ImageType.ChannelProfile
                            ? "round"
                            : "rect"
                    }
                />
            )}
        </Box>
    );
};

export default UploadCoverDialogContent;
